import { ViperDatePicker } from "@/components";
import * as S from "@/containers/Project/components/styled";
import { useIsAdmin } from "@/helpers/permissions";
import { useMobileLayout } from "@/hooks/useAppMedia";
import {
  useGetBackdatingSettingsQuery,
  useUpdateProjectMilestoneActualDateMutation,
  useUpdateProjectMilestoneTargetDateMutation,
} from "@/services";
import { Alert, Spin } from "antd";
import dayjs from "dayjs";
import { bool, func, object, string } from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

const DEFAULT_BACKFILL_LIMIT_DAYS = 30;

const Spinner = <Spin style={{ marginBottom: "-4px" }} size="small" />;

const getErrorMessage = (error, intl) => {
  let errorMessage = intl.formatMessage({
    id: "general.toastSomethingWentWrong",
  });

  if (error?.data) {
    if (typeof error.data === "object") {
      errorMessage =
        error.data.message ||
        error.data.detail ||
        error.data.title ||
        errorMessage;
    } else if (typeof error.data === "string") {
      errorMessage = error.data;
    }
  } else if (error?.message) {
    errorMessage = error.message;
  }

  return errorMessage;
};

export const MilestoneDateEditor = ({
  milestone,
  intlId,
  datePropertyName,
  disabled,
  onSaved,
}) => {
  const intl = useIntl();
  const isMobileLayout = useMobileLayout();
  const isAdmin = useIsAdmin();
  const { data: backdatingSettings } = useGetBackdatingSettingsQuery();
  const dateBackfillLimit =
    backdatingSettings?.maxBackdateDays ?? DEFAULT_BACKFILL_LIMIT_DAYS;
  const [date, setDate] = useState();
  const [isSyncing, setIsSyncing] = useState(false);
  const id = `milestone-${milestone.id}-date-picker-${datePropertyName}`;

  const previousValidDateRef = useRef(undefined);

  const lastChangeTimeRef = useRef(0);

  const [updateTargetDate, { isLoading: savingTargetDate }] =
    useUpdateProjectMilestoneTargetDateMutation();
  const [updateActualDate, { isLoading: savingActualDate }] =
    useUpdateProjectMilestoneActualDateMutation();

  const isTargetDate = datePropertyName === "targetDate";
  const saving =
    (isTargetDate ? savingTargetDate : savingActualDate) || isSyncing;

  const earliestAllowedDate = () =>
    dayjs().subtract(dateBackfillLimit, "day").startOf("day");

  useEffect(() => {
    const dateValue = (milestone || {})[datePropertyName];
    const dateObj = dateValue && dayjs(dateValue);
    setDate(dateObj);
    previousValidDateRef.current = dateObj;
  }, [datePropertyName, milestone]);


  const validateDate = (dateValue) => {
    // Admin users can set any date
    if (isAdmin) {
      return true;
    }

    // Allow clearing the date
    if (!dateValue) {
      return true;
    }

    const dateToValidate = dayjs(dateValue);

    if (!dateToValidate.isValid()) {
      return true;
    }

    const isInvalid = dateToValidate
      .startOf("day")
      .isBefore(earliestAllowedDate(), "day");

    if (isInvalid) {
      toast.error(
        intl.formatMessage(
          { id: "milestonesList.editDateWarning" },
          { count: dateBackfillLimit }
        )
      );
      return false;
    }

    return true;
  };

  const saveDate = async (value) => {
    if (!validateDate(value)) {
      return;
    }

    const mutate = isTargetDate ? updateTargetDate : updateActualDate;
    const payload = {
      projectId: milestone.projectId,
      milestoneId: milestone.id,
      ...(isTargetDate
        ? { targetDate: value ? new Date(value) : value }
        : { actualDate: value }),
    };

    setIsSyncing(true);
    try {
      const projectMilestone = await mutate(payload).unwrap();

      const savedValue = projectMilestone?.date;
      const savedDateObj = savedValue ? dayjs(savedValue) : null;
      lastChangeTimeRef.current = Date.now();
      setDate(savedDateObj);
      previousValidDateRef.current = savedDateObj;

      await onSaved?.(projectMilestone);
    } catch (error) {
      toast.error(getErrorMessage(error, intl));
    } finally {
      setIsSyncing(false);
    }
  };

  const handleChange = async (value) => {
    if (!value) {
      
      previousValidDateRef.current = null;
      setDate(value);
      await saveDate(value);
      return;
    }

    // Ensure value is a dayjs object (handle cases where user types directly)
    const dateObj = dayjs(value);

    // Check if date is valid (handles invalid date formats when user types)
    if (!dateObj.isValid()) {
      // Invalid date format, reset to previous value
      const previousValue = previousValidDateRef.current;
      setDate(previousValue ? dayjs(previousValue) : null);
      return;
    }

    // Date is valid, proceed with change
    // disabledDate prop handles backdating limit validation in the picker
    previousValidDateRef.current = value;
    lastChangeTimeRef.current = Date.now();
    setDate(value);
    await saveDate(value);
  };

  return (
    <S.StyledDateValueContainer $mobile={isMobileLayout}>
      <S.StyledDateValueLabel $mobile={isMobileLayout}>
        {intl.formatMessage({
          id: intlId,
        })}
      </S.StyledDateValueLabel>
      <S.StyledDateValue>
        <ViperDatePicker
          id={id}
          data-test-id={id}
          onChange={handleChange}
          disabled={disabled || saving}
          value={date}
          suffixIcon={saving ? Spinner : undefined}
          disabledDate={(current) =>
            !isAdmin &&
            current &&
            current.isBefore(earliestAllowedDate(), "day")
          }
          renderExtraFooter={() =>
            !isAdmin && (
              <Alert
                type="warning"
                message={intl.formatMessage(
                  {
                    id: "milestonesList.editDateWarning",
                  },
                  {
                    count: dateBackfillLimit,
                  }
                )}
              />
            )
          }
        />
      </S.StyledDateValue>
    </S.StyledDateValueContainer>
  );
};

MilestoneDateEditor.propTypes = {
  milestone: object,
  intlId: string,
  datePropertyName: string,
  disabled: bool,
  onSaved: func,
};
